import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { usePrices, getBundles } from '../hooks/usePrices';
import { useLang } from '../LangContext';

/**
 * BundleFlyout — site-wide attention magnet for the Spar-Bundles.
 *
 * Lives in the global Layout. It only renders after the visitor has
 * scrolled past the hero (or 600px on non-home pages), then appears
 * as a vertical tab on the right edge. Clicking the tab opens a
 * compact panel with the bundle list + prices and a CTA that scrolls
 * the home page's bundles section into view (or navigates there).
 *
 * Behaviour:
 *   • idle: vertical tab pulses gently to attract attention
 *   • hover/tap: panel slides in from the right
 *   • outside-tap or close button: panel slides back, tab remains
 *   • bundles disabled in prices.json → renders nothing
 */
export default function BundleFlyout() {
  const { lang } = useLang();
  const prices = usePrices();
  const bundles = React.useMemo(() => getBundles(prices, lang), [prices, lang]);
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  // Reveal the flyout only once the visitor has scrolled past the hero.
  // On the home page the hero is ~100vh; on other pages we use a flat
  // 600px threshold which is roughly past the page header.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const homeThreshold = () => {
      const hero = document.querySelector('[data-testid="hero-section"]');
      return hero ? hero.offsetHeight - 80 : 600;
    };
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset;
      const threshold = homeThreshold();
      setVisible(y > threshold);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Click-away closes the panel.
  useEffect(() => {
    if (!open) return;
    const onAway = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onAway);
    document.addEventListener('touchstart', onAway, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onAway);
      document.removeEventListener('touchstart', onAway);
    };
  }, [open]);

  if (!bundles || !bundles.items.length) return null;

  // Panel width — full width minus tab on small screens, fixed 320 on larger.
  const panelWidth = (typeof window !== 'undefined' && window.innerWidth < 480)
    ? Math.max(220, window.innerWidth - 56)
    : 320;

  const onCTAClick = () => {
    setOpen(false);
    // Defer the scroll so the close animation has a frame to start.
    requestAnimationFrame(() => {
      const target = document.querySelector('[data-testid="bundles-section"]');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          ref={panelRef}
          initial={{ x: 60, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 60, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center"
          data-testid="bundle-flyout"
        >
          {/* Vertical tab — always rendered, pulses to attract attention.
              Mobile: a slim no-text strip so it doesn't cover content.
              Desktop (sm+): full pill with vertical eyebrow + count badge. */}
          <motion.button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="bundle-flyout-tab"
            aria-expanded={open}
            aria-label={bundles.eyebrow}
            className="relative flex flex-col items-center justify-center gap-2 sm:gap-3 py-4 sm:py-5 px-1.5 sm:px-3 rounded-l-2xl bg-[#F26B1A] text-white shadow-[0_18px_44px_-16px_rgba(242,107,26,0.7)] hover:bg-[#D85A0A] transition-colors focus:outline-none"
            animate={open ? { scale: 1 } : { scale: [1, 1.06, 1, 1.06, 1] }}
            transition={open
              ? { duration: 0.3 }
              : { duration: 2.4, repeat: Infinity, times: [0, 0.4, 0.5, 0.9, 1], ease: 'easeInOut' }
            }
            style={{ transformOrigin: 'right center' }}
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            <span
              className="hidden sm:inline text-[11.5px] font-bold tracking-[0.28em] uppercase whitespace-nowrap"
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              {bundles.eyebrow}
            </span>
            {/* Mobile chevron — directional cue without taking space */}
            <svg
              className="sm:hidden w-3 h-3"
              viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M15 6l-6 6 6 6" />
            </svg>
            <span className="hidden sm:inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/15 text-[11px] font-bold tabular-nums">
              {bundles.items.length}
            </span>
          </motion.button>

          {/* Slide-in panel */}
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: panelWidth, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden rounded-l-2xl bg-white/95 backdrop-blur-md border border-r-0 border-[#F26B1A]/30 shadow-[0_24px_60px_-20px_rgba(31,29,26,0.35)]"
                data-testid="bundle-flyout-panel"
              >
                <div style={{ width: panelWidth }} className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-[10.5px] uppercase tracking-[0.24em] font-bold text-[#F26B1A]">
                        {bundles.eyebrow}
                      </p>
                      <h3 className="font-display text-[20px] leading-tight text-[#1F1D1A] mt-0.5">
                        {bundles.title}
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      aria-label="Close"
                      data-testid="bundle-flyout-close"
                      className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-[#5C5852] hover:text-[#1F1D1A] hover:bg-black/5 transition-colors"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <ul className="space-y-2.5 mb-4">
                    {bundles.items.map((b) => (
                      <li
                        key={b.id}
                        className={`flex items-baseline justify-between gap-3 pb-2 border-b border-[#E5E0D2] last:border-b-0 ${b.soldOut ? 'opacity-50' : ''}`}
                      >
                        <div className="min-w-0">
                          <p className="text-[13.5px] font-semibold text-[#1F1D1A] truncate flex items-center gap-1.5">
                            {b.name}
                            {b.tagBadge && !b.soldOut && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded-full text-white text-[9px] uppercase tracking-[0.12em] font-bold whitespace-nowrap"
                                style={{ backgroundColor: b.tagBadge.color }}
                              >
                                {b.tagBadge.label}
                              </span>
                            )}
                          </p>
                          {b.save > 0 && (
                            <p className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#F26B1A] mt-0.5">
                              {bundles.saveLabel} {b.displaySave} · −{b.savePct}%
                            </p>
                          )}
                        </div>
                        <span className="text-[15.5px] font-bold tabular-nums text-[#1F1D1A] whitespace-nowrap">
                          {b.displayPrice}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/"
                    onClick={onCTAClick}
                    data-testid="bundle-flyout-cta"
                    className="block w-full text-center px-4 py-2.5 rounded-full bg-[#F26B1A] text-white text-[13px] font-semibold tracking-wide uppercase hover:bg-[#D85A0A] transition-colors"
                  >
                    {bundles.ctaLabel}
                  </Link>

                  {(bundles.whiteStandardNote || bundles.vatNote || bundles.shippingNote) && (
                    <div
                      data-testid="bundle-flyout-disclaimer"
                      className="mt-3 text-[10.5px] leading-snug text-[#7A746B] space-y-1"
                    >
                      {bundles.whiteStandardNote && <p>* {bundles.whiteStandardNote}</p>}
                      {(bundles.vatNote || bundles.shippingNote) && (
                        <p>
                          {bundles.vatNote}{bundles.vatNote && bundles.shippingNote ? ' · ' : ''}{bundles.shippingNote}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
